import { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import EventHostedCard from "../../components/EventHostedCard";
import Button from "../../components/Button/Button";
import { useLocation, useNavigate } from "react-router-dom";
import Typography from "../../components/Typography";
import { useEventContext } from "../../context/EventContext";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../theme";
import Banner from "../../components/Banner";
import CloseIcon from "../../components/Icons/CloseIcon";
import StripeConnectIcon from "../../components/Icons/StripeConnectIcon";
import ArrowRightUpIcon from "../../components/Icons/ArrowRightUpIcon";
import AddIcon from "../../components/Icons/AddIcon";
import EventsTimeFilter from "../../components/EventsTimeFilter";
import SpinnerCircular from "../../components/SpinnerCircular";
import { openUserSettingsModal } from "../../utils/settingsNavigation";

const MyEventsOrganized = () => {
	// Obtenemos los eventos organizados del contexto
	const { myEvents, changeView, currentView, loading } = useEventContext();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation("hostedEvents");
	const { user } = useAuth();

	// Estado para controlar si el banner está cerrado
	const [isBannerClosed, setIsBannerClosed] = useState(false);

	// Función para navegar a la configuración de pagos
	const handleConfigurePayments = () => {
		const targetUserId = user?.id || user?._id;
		if (!targetUserId) {
			return;
		}

		openUserSettingsModal({ navigate, location, userId: targetUserId });
	};

	// Cargar el estado del banner desde localStorage al montar el componente
	useEffect(() => {
		const bannerClosed = localStorage.getItem("paymentBannerClosed");
		if (bannerClosed === "true") {
			setIsBannerClosed(true);
		}
	}, []);

	const handleCreateEvent = () => {
		navigate("/create-event");
	};

	// Función para cerrar el banner y guardar el estado
	const handleCloseBanner = () => {
		setIsBannerClosed(true);
		localStorage.setItem("paymentBannerClosed", "true");
	};

	// Usamos la parte "organized" del contexto
	const { organized } = myEvents || { organized: { upcoming: [], past: [] } };

	// Para manejar la pestaña activa usamos el currentView del contexto
	const [activeSubTab, setActiveSubTab] = useState(currentView || "upcoming");

	// Función para manejar el cambio de pestaña
	const handleTabChange = async (newTab) => {
		setActiveSubTab(newTab);
		await changeView(newTab);
	};

	// Sincronizar activeSubTab con currentView cuando cambie
	useEffect(() => {
		if (currentView && currentView !== activeSubTab) {
			setActiveSubTab(currentView);
		}
	}, [currentView, activeSubTab]);

	// Estado para controlar si mostrar eventos cancelados
	const [showCancelledEvents, setShowCancelledEvents] = useState(["exclude"]);

	return (
		<>
			<MyEventsList>
				<Container>
					{!isBannerClosed && (
						<Banner>
							<BannerWrapper>
								<BannerContainer>
									<BannerIconWrapper>
										<StripeConnectIcon />
									</BannerIconWrapper>
									<BannerContent>
										<Typography
											as="h1"
											$variant="title-5-semibold"
											$variantTablet="title-5-semibold"
											$variantMobile="body-1-semibold"
										>
											{t("banner.title")}
										</Typography>
										<Typography
											as="p"
											$variant="body-1-medium"
											$variantTablet="body-2-medium"
											$variantMobile="body-2-medium"
											color={theme.colors.defaultWeak}
										>
											{/* Cobra los pagos creando una cuenta Stripe. Recibe pagos diarios. Configúralo en menos de 5 minutos. */}
											{t("banner.description")}
										</Typography>
										<BannerActions>
											<Button
												$variant=""
												size="m"
												sizeTablet="m"
												sizeMobile="m"
												onClick={handleConfigurePayments}
											>
												{t("banner.setupButton")}
											</Button>
											<Button
												$variant="ghost"
												size="m"
												sizeTablet="m"
												sizeMobile="m"
											>
												{t("banner.helpButton")}
												<ArrowRightUpIcon
													height={20}
													width={20}
													fill={theme.colors.defaultMain}
												/>
											</Button>
										</BannerActions>
									</BannerContent>
								</BannerContainer>
								<Button
									$variant="ghost"
									size="l"
									sizeTablet="l"
									sizeMobile="m"
									$iconOnly
									onClick={handleCloseBanner}
								>
									<CloseIcon
										height={20}
										width={20}
										fill={theme.colors.defaultMain}
									/>
								</Button>
							</BannerWrapper>
						</Banner>
					)}
					<SectionHeader>
						<HeaderInner>
							<HeaderBlock>
								<Typography
									as="h1"
									$variant="title-4-semibold"
									$variantTablet="title-4-semibold"
									$variantMobile="body-1-semibold"
								>
									{t("organized.title")}
								</Typography>
								<Typography
									as="p"
									$variant="body-1-medium"
									$variantTablet="body-2-medium"
									$variantMobile="body-2-medium"
									color={theme.colors.defaultWeak}
								>
									{t("organized.description")}
								</Typography>
							</HeaderBlock>
							<AddEventButton $variant="outline" onClick={handleCreateEvent}>
								<AddIcon
									height={20}
									width={20}
									fill={theme.colors.defaultStrong}
								/>
							</AddEventButton>
						</HeaderInner>
						<HeaderFiltersWrapper>
							<EventsTimeFilter
								activeTab={activeSubTab}
								onTabChange={handleTabChange}
								showCancelledEvents={showCancelledEvents}
								onCancelledEventsChange={setShowCancelledEvents}
								upcomingLabel={t("organized.tabs.upcoming")}
								pastLabel={t("organized.tabs.past")}
								includeCancelledLabel={t("organized.filters.includeCancelled")}
								excludeCancelledLabel={t("organized.filters.excludeCancelled")}
							/>
						</HeaderFiltersWrapper>
					</SectionHeader>
					{loading ? (
						<LoadingContainer>
							<SpinnerCircular
								size={30}
								thickness={180}
								speed={100}
								color={theme.fill.brandMain}
								secondaryColor={theme.fill.defaultWeak}
							/>
						</LoadingContainer>
					) : (
						<>
							{activeSubTab === "upcoming" && (
								<MyFutureEvents>
									{(() => {
										const filteredEvents = organized.upcoming
											? organized.upcoming.filter((event) => {
													if (showCancelledEvents[0] === "exclude") {
														return event.status !== "cancelled";
													}
													return true; // incluir todos los eventos
												})
											: [];

										return filteredEvents.length > 0 ? (
											<EventListWrapper>
												{filteredEvents.map((event) => (
													<EventHostedCard key={event.id} event={event} />
												))}
											</EventListWrapper>
										) : (
											<EmptyState>
												<EmptyStateHeader>
													<Typography
														as="p"
														$variant="title-4-semibold"
														color={theme.colors.defaultMain}
													>
														{t("organized.emptyState.title")}
													</Typography>
													<Typography
														as="p"
														$variant="body-1-medium"
														color={theme.colors.defaultWeak}
													>
														{t("organized.emptyState.description")}
													</Typography>
												</EmptyStateHeader>
												<Button
													onClick={handleCreateEvent}
													size="l"
													sizeTablet="l"
													sizeMobile="m"
												>
													<AddIcon
														height={20}
														width={20}
														fill={theme.colors.inverseMain}
													/>
													{t("organized.emptyState.createButton")}
												</Button>
											</EmptyState>
										);
									})()}
								</MyFutureEvents>
							)}

							{activeSubTab === "past" && (
								<MyPastEvents>
									{(() => {
										const filteredEvents = organized.past
											? organized.past.filter((event) => {
													if (showCancelledEvents[0] === "exclude") {
														return event.status !== "cancelled";
													}
													return true; // incluir todos los eventos
												})
											: [];

										return filteredEvents.length > 0 ? (
											<EventListWrapper>
												{filteredEvents.map((event) => (
													<EventHostedCard key={event.id} event={event} />
												))}
											</EventListWrapper>
										) : (
											<EmptyState>
												<EmptyStateHeader>
													<Typography
														as="p"
														$variant="title-4-semibold"
														color={theme.colors.defaultMain}
													>
														{t("organized.emptyState.title")}
													</Typography>
													<Typography
														as="p"
														$variant="body-1-medium"
														color={theme.colors.defaultWeak}
													>
														{t("organized.emptyState.description")}
													</Typography>
												</EmptyStateHeader>
												<Button
													onClick={handleCreateEvent}
													size="l"
													sizeTablet="l"
													sizeMobile="m"
												>
													<AddIcon
														height={20}
														width={20}
														fill={theme.colors.inverseMain}
													/>
													{t("organized.emptyState.createButton")}
												</Button>
											</EmptyState>
										);
									})()}
								</MyPastEvents>
							)}
						</>
					)}
				</Container>
			</MyEventsList>
		</>
	);
};

export default MyEventsOrganized;

const EmptyState = styled.div`
  display: flex;
  padding: 80px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-sm, 16px);
  align-self: stretch;
  border-radius: 16px;
  border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
`;

const EmptyStateHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding:  ${({ theme }) => theme.sizing.md};
  width: 100%;
  max-width: 1400px;
  gap: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;

const HeaderInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  flex-grow: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: space-between;
  }
`;

const HeaderFiltersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const AddEventButton = styled(Button)`
  border-radius: 40px;
  height: 32px;
  width: 32px;
`;

// const Tabs = styled.div`
//   display: flex;
//   padding: ${({ theme }) => theme.sizing.xxs};
//   align-items: flex-start;
//   // gap: ${({ theme }) => theme.sizing.xs};
//   border-radius: ${({ theme }) => theme.radius.xl};
//   background: ${({ theme }) => theme.fill.defaultWeak};

//   @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
//     flex-grow: 1;
//   }
// `;

// const ButttonTab = styled(Button)`
//   border-radius: ${({ theme }) => theme.radius.xl};
//   background-color: transparent;
//   transition: all 0.3s ease-in-out;
//   color: ${({ theme }) => theme.colors.defaultWeak};
//   padding-left: ${({ theme }) => theme.sizing.sm};
//   padding-right: ${({ theme }) => theme.sizing.sm};

//   &.Active {
//     background-color: ${({ theme }) => theme.fill.defaultMain};
//     color: ${({ theme }) => theme.colors.defaultMain};
//     box-shadow: 0px 0px 4px rgba(0,0,0,0.08), 0px 4px 12px rgba(0,0,0,0.04);
//   }

//   @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
//     font-size: 14px;
//   }

//   @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
//     flex-grow: 1;
//   }
// `;

const MyEventsList = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: center;
`;
const MyFutureEvents = styled.div`
`;

const EventListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm}
`;

const MyPastEvents = styled.div`
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 0px;
    flex-direction: column;
    align-items: flex-start;
  }
  
`;

const BannerIconWrapper = styled.div`
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  background-color: ${({ theme }) => theme.fill.defaultAlphaMain16};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.md};
  height: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};

    svg {
      width: 80px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border: none;
    padding: 0px;
    height: auto;
    gap: ${({ theme }) => theme.sizing.xs};
    padding-left: ${({ theme }) => theme.sizing.xs};
  }

`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding: ${({ theme }) => theme.sizing.xs} 0px; 

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.sizing.xs};
  }
`;

const BannerActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  align-items: center;
  flex-shrink: 0;
  padding-top: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-grow: 1;
    width: 100%;
  }
`;

const BannerWrapper = styled.div`
  background: ${({ theme }) => theme.fill.brandAlphaMain8};
  border-radius: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: row;
  padding: ${({ theme }) => theme.sizing.xs};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.sizing.xl};
`;
